/**
 * The home page's rows that are not category groups: For You's special rows
 * and the visitor's first page of tag rows, ranked in with the groups by
 * utils/home-row-rank.ts buildHomeRows.
 *
 * Rows that need history (recently viewed, wishlist, searches, liked, bought,
 * tags) come back empty for a visitor without it, so they only show for a
 * visitor who has the signal. The global rows (trending, new arrivals, deals,
 * holiday) show for everyone.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserProvider';
import { getAnalytics } from '../utils/analytics';
import { readString } from '../utils/guarded-storage';
import { useSpecialShowcases } from './useSpecialShowcases';
import { useTagShowcases } from './useTagShowcases';
import { PAGE_SIZE } from '../data/js/pagination';
import { isPageEnteredByBack } from '../utils/navigation-direction';

type SpecialRows = ReturnType<typeof useSpecialShowcases>['showcases'];
type TagRows = ReturnType<typeof useTagShowcases>['showcases'];

/**
 * The extra rows home last showed, for Back. The page waits for them before it
 * paints its rows, so refetching them on Back held the skeleton for seconds
 * (2026-10-26). In memory: Back within the tab; a reload fetches. The grid's
 * list is kept the same way (utils/browse-list-cache.ts), and the category
 * rows by useHomeShowcases.
 */
let kept: { specials: SpecialRows; tagRows: TagRows } | null = null;

/** Test seam. */
export function clearHomeExtraRowsMemory(): void {
  kept = null;
}

/**
 * Every special kind except near_you, which home places as its own
 * self-fetching row (components/search-listing/NearYouShowcase.tsx).
 */
export const HOME_SPECIAL_TYPES =
  'recently_viewed,trending,new_arrivals,best_deals,your_wishlist,recent_searches,holiday,similar_to_liked,similar_to_purchased';

export function useHomeExtraRows(enabled: boolean) {
  const { profile: user, loggedIn } = useUser();
  // Back to home: what it showed, at once, with nothing fetched.
  const [restored] = useState(() => (isPageEnteredByBack() ? kept : null));
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [anonymousId, setAnonymousId] = useState<string | undefined>();
  const [identityChecked, setIdentityChecked] = useState(false);

  useEffect(() => {
    if (!enabled || identityChecked) return;
    // Creates the session and anonymous ids on a first visit.
    getAnalytics();
    const sid = readString('session', 'sw_session_id');
    const aid = readString('local', 'sw_anonymous_id');
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the ids live in browser storage, read after mount; reading during render would mismatch hydration
    if (sid) setSessionId(sid);
    if (aid) setAnonymousId(aid);
    setIdentityChecked(true);
  }, [enabled, identityChecked]);

  const userId = typeof user?.id === 'string' ? parseInt(user.id, 10) : user?.id;
  const hasIdentifier = loggedIn ? !!userId : !!sessionId;
  const type = loggedIn ? 'user' : 'session';

  const specials = useSpecialShowcases({
    type,
    userId,
    sessionId,
    anonymousId,
    days: 30,
    limit: PAGE_SIZE.SHOWCASE,
    showcaseTypes: HOME_SPECIAL_TYPES,
    enabled: enabled && identityChecked && !restored,
  });

  const tags = useTagShowcases({
    type,
    userId,
    sessionId,
    anonymousId,
    days: 30,
    listingsPerTag: PAGE_SIZE.SHOWCASE,
    minListingsPerTag: 4,
    enabled: enabled && identityChecked && hasIdentifier && !restored,
  });

  /** Everything the first screen ranks has arrived (or failed). */
  const ready =
    !enabled || !!restored || (identityChecked && specials.hasLoaded && (!hasIdentifier || tags.hasLoaded));
  // First page only: home's later screens are category rows.
  const fetchedTagRows = useMemo(() => tags.showcases.slice(0, tags.firstPageCount || tags.showcases.length), [tags.showcases, tags.firstPageCount]);

  useEffect(() => {
    if (enabled && ready && !restored) kept = { specials: specials.showcases, tagRows: fetchedTagRows };
  }, [enabled, ready, restored, specials.showcases, fetchedTagRows]);

  // Handed out only when something was restored (below).
  const keptOne = useCallback((type: SpecialRows[number]['type']) => restored!.specials.find(s => s.type === type), [restored]);
  const keptMany = useCallback((type: SpecialRows[number]['type']) => restored!.specials.filter(s => s.type === type), [restored]);

  if (restored) return { ready, getShowcase: keptOne, getShowcases: keptMany, tagRows: restored.tagRows };
  return {
    ready,
    getShowcase: specials.getShowcase,
    getShowcases: specials.getShowcases,
    tagRows: fetchedTagRows,
  };
}
